import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";

function formatDate(value) {
  if (!value) return "Recently added";
  try { return new Date(value).toLocaleDateString(); } catch { return "Recently added"; }
}

function formatFileSize(value) {
  const bytes = Number(value || 0);
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DownloadCenter() {
  const [downloads, setDownloads] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api("/api/downloads")
      .then((data) => setDownloads(Array.isArray(data) ? data : []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    return [...new Set(downloads.map((item) => item.category).filter(Boolean))].sort();
  }, [downloads]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return downloads.filter((item) => {
      const matchesCategory = !category || item.category === category;
      const text = [item.title, item.category, item.description, item.audience].filter(Boolean).join(" ").toLowerCase();
      return matchesCategory && text.includes(q);
    });
  }, [downloads, query, category]);

  async function countAndOpen(item) {
    try { await api(`/api/downloads/${item.id}/count`, { method: "POST" }); } catch {}
    window.open(item.file_url, "_blank", "noopener,noreferrer");
    setDownloads((prev) => prev.map((row) => row.id === item.id ? { ...row, download_count: Number(row.download_count || 0) + 1 } : row));
  }

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-16">
      <section className="mx-auto max-w-6xl">
        <p className="text-sm font-bold uppercase tracking-[0.25em] text-emerald-700">Resource Centre</p>
        <h1 className="mt-3 text-4xl font-black text-slate-900">Download Center</h1>
        <p className="mt-3 max-w-2xl text-slate-600">
          Download official forms, academic calendars, timetables, student guides, admission documents and institutional resources.
        </p>

        <div className="mt-8 grid gap-3 md:grid-cols-[1fr_260px]">
          <input
            className="w-full rounded-2xl border border-slate-300 bg-white p-4"
            placeholder="Search downloads by title, category or description..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-2xl border border-slate-300 bg-white p-4">
            <option value="">All categories</option>
            {categories.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>

        {error && <div className="mt-6 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}

        {loading ? (
          <div className="mt-8 rounded-3xl bg-white p-8 text-slate-500 shadow-sm">Loading downloads...</div>
        ) : filtered.length === 0 ? (
          <div className="mt-8 rounded-3xl bg-white p-8 text-slate-500 shadow-sm">No published downloads found.</div>
        ) : (
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((item) => (
              <article key={item.id} className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-1 hover:shadow-lg">
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">{item.category}</span>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold capitalize text-slate-600">{item.audience || "all"}</span>
                </div>
                <h2 className="mt-4 text-xl font-black text-slate-900">{item.title}</h2>
                {item.description && <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{item.description}</p>}
                <div className="mt-5 flex flex-wrap gap-3 text-xs font-bold text-slate-500">
                  <span>{item.file_type || "file"}</span>
                  {formatFileSize(item.file_size) && <span>{formatFileSize(item.file_size)}</span>}
                  <span>{formatDate(item.created_at)}</span>
                </div>
                <p className="mt-3 text-sm text-slate-500">Downloaded {item.download_count || 0} times</p>
                <button onClick={() => countAndOpen(item)} className="mt-5 rounded-xl bg-slate-950 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-700">
                  Download Document
                </button>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
